import { CommonModule } from "@angular/common";
import { Component, inject, Inject } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { Order, OrderTicket } from "@models/order";
import { TicketStatusEnum } from "@models/ticket-order";
import { OrderPaymentStatusPipe } from "@shared/pipes/order-payment-status.pipe";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { DialogHeaderComponent } from "../../../../shared/components/dialog-header/dialog-header.component";

export interface OrderDetailsDialogData {
  order: Order;
}

@Component({
  selector: "app-order-details-dialog",
  imports: [
    CommonModule,
    ButtonComponent,
    MatDialogModule,
    DialogHeaderComponent,
    OrderPaymentStatusPipe,
  ],
  templateUrl: "./order-details-dialog.component.html",
  styleUrl: "./order-details-dialog.component.scss",
})
export class OrderDetailsDialogComponent {
  readonly dialogRef = inject(MatDialogRef<OrderDetailsDialogComponent>);

  order: Order;

  constructor(@Inject(MAT_DIALOG_DATA) public data: OrderDetailsDialogData) {
    this.order = data.order;
  }

  get customerName(): string {
    return this.getHolderName(this.order.tickets?.[0]);
  }

  get customerEmail(): string {
    return this.order.tickets?.[0]?.user_email?.trim() || "Não informado";
  }

  getHolderName(ticket?: OrderTicket): string {
    return (
      [ticket?.user_name, ticket?.user_last_name]
        .filter((part) => typeof part === "string" && part.trim())
        .map((part) => part!.trim())
        .join(" ") || (this.order.pdv ? "Ao portador" : "Não informado")
    );
  }

  getTicketFee(ticket: OrderTicket): number | null {
    return this.order.pdv
      ? ticket.pdv_fee_cents == null
        ? null
        : ticket.pdv_fee_cents / 100
      : Number(ticket.tax);
  }

  displayPaymentMethod(payment_method: string): string {
    switch (payment_method) {
      case "credit_card":
        return "Cartão de crédito";
      case "billet":
        return "Boleto";
      case "pix":
        return "Pix";
      case "cash":
        return "Dinheiro";
      default:
        return payment_method;
    }
  }

  getTicketTotal(ticket: OrderTicket) {
    return (
      Number(ticket.price) +
      (this.order.pdv
        ? (ticket.pdv_customer_fee_cents ?? 0) / 100
        : Number(ticket.tax))
    );
  }

  get hasPdvCustomerFee(): boolean {
    return this.order.tickets.some(
      (ticket) => (ticket.pdv_customer_fee_cents ?? 0) > 0,
    );
  }

  getTicketStatus(ticket: OrderTicket): string {
    switch (String(ticket.status)) {
      case TicketStatusEnum.Active:
        return "Ativo";
      case TicketStatusEnum.AlreadyUsed:
      case "already_used":
        return "Já utilizado";
      case TicketStatusEnum.TransferInProgress:
        return "Transferência pendente";
      case "received_by_transfer":
        return "Recebido por transferência";
      case "no issues":
        return "Pronto para usar! 🥳";
      case "transferred":
        return "Ingresso transferido";
      case TicketStatusEnum.Cancelled:
        return "Cancelado";
      case "Payment not completed":
        return "Aguardando pagamento";
      case "Missing user information":
        return "Faltam informações do usuário";
      case "Pending issues":
        return "Em análise, em breve você terá novidades";
      default:
        return "Indefinido";
    }
  }
}
